import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import Icon from '../components/icons/Icon';
import config from '../config';
import { listRuns, getRun, startRun, cancelRun } from '../services/WorkflowsService';
import './Agents.css';

/**
 * Door 3 of the front-door proposal (https://claude.ai/code/artifact/6c96ea9c-51ac-4d25-9c4f-a94b121348d4):
 * operate background agents someone else configured in ai-foundry — trigger a
 * run, watch it, cancel it. Never authors a workflow; that stays
 * ai-foundry-website's job (see the "Configure in AI Foundry" link below).
 *
 * UX: presented as one scrolling conversation instead of a static list + a
 * separate "start" form — each run is a user turn ("run <workflow>") answered
 * by an assistant turn (a live status card), same mixed-timeline idea as the
 * Chat door and the animated-waiting-words/expandable-detail pattern already
 * built there (task #17) — so triggering and watching a workflow feels like
 * continuing a conversation, not filling out a form.
 *
 * "Type a workflow name to run" is still a manual name entry, not a picker,
 * because there is no workflow-definitions-listing endpoint proxied yet —
 * front-door proposal §07 decision 2, still open. See KEYCLOAK_NEW_CONFIGS.md
 * for the other gap (the workflows-api token-exchange alias) blocking this
 * from working for real outside WORKFLOWS_API_DEV_BYPASS.
 */

const STATUS_LABEL = {
  running: 'Running',
  completed: 'Done',
  failed: 'Failed',
  cancelled: 'Cancelled',
  waiting: 'Waiting on approval',
  terminated: 'Terminated',
  timed_out: 'Timed out',
  starting: 'Starting…',
};

const ACTIVE_STATUSES = new Set(['running', 'waiting', 'starting']);

const formatWhen = (iso) => {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

/** One assistant "turn": a run's live status card, expandable to see its steps. */
function RunCard({ run, onCancel, cancelling }) {
  const [expanded, setExpanded] = useState(false);
  const [detail, setDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  const statusKey = (run.status || '').toLowerCase();
  const isActive = ACTIVE_STATUSES.has(statusKey);
  const isPending = statusKey === 'starting';

  const toggleExpanded = async () => {
    const next = !expanded;
    setExpanded(next);
    if (next && !detail && run.runId && !isPending) {
      setLoadingDetail(true);
      try {
        const full = await getRun(run.runId);
        setDetail(full);
      } catch {
        setDetail({ error: 'Could not load run detail' });
      } finally {
        setLoadingDetail(false);
      }
    }
  };

  return (
    <div className="agent-message agent-message--assistant">
      <div className="agent-avatar">
        <Icon name="agents" size={16} />
      </div>
      <div className={`agent-run-card${isPending ? ' agent-run-card--pending' : ''}`}>
        <button className="agent-run-card-header" onClick={toggleExpanded} type="button">
          <div className="agent-run-card-title">
            <span className="agent-run-name">{run.workflow || run.workflowId}</span>
            <span className={`agent-run-status agent-run-status--${statusKey}`}>
              {isPending && <span className="agent-run-status-dot" />}
              {STATUS_LABEL[statusKey] || run.status}
            </span>
          </div>
          <svg
            className={`agent-run-chevron${expanded ? ' agent-run-chevron--open' : ''}`}
            fill="none" height="14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
            viewBox="0 0 24 24" width="14"
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>
        <div className="agent-run-meta">
          {run.trigger && <span>{run.trigger}</span>}
          {run.startedAt && <span>{'started ' + formatWhen(run.startedAt)}</span>}
        </div>

        {expanded && (
          <div className="agent-run-detail">
            {loadingDetail ? (
              <div className="agent-run-detail-loading">Loading run detail…</div>
            ) : detail?.error ? (
              <div className="agent-run-detail-error">{detail.error}</div>
            ) : isPending ? (
              <div className="agent-run-detail-loading">Waiting for the run to be accepted…</div>
            ) : detail?.steps?.length > 0 ? (
              <ul className="agent-run-steps">
                {detail.steps.map((step, i) => (
                  <li className="agent-run-step" key={step.id || step.name || i}>
                    <span className={`agent-run-step-dot agent-run-step-dot--${(step.status || '').toLowerCase()}`} />
                    <span className="agent-run-step-name">{step.name || step.id || `Step ${i + 1}`}</span>
                    {step.status && <span className="agent-run-step-status">{step.status}</span>}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="agent-run-detail-loading">No step detail reported for this run.</div>
            )}
            {detail?.error && typeof detail.error === 'object' && (
              <pre className="agent-run-error-block">{JSON.stringify(detail.error, null, 2)}</pre>
            )}
            {detail?.output && (
              <pre className="agent-run-output-block">{JSON.stringify(detail.output, null, 2)}</pre>
            )}
          </div>
        )}

        {isActive && !isPending && (
          <button
            className="agent-run-cancel"
            disabled={cancelling}
            onClick={(e) => { e.stopPropagation(); onCancel(run.workflowId); }}
            type="button"
          >
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * `readOnly` (task #134): when embedded as the "Agent" tab inside a project's
 * Code view, the real chat panel (VfsChatPanel.jsx) already has /run, /runs,
 * /cancel slash commands wired to the exact same WorkflowsService this
 * component uses — trigger/watch/cancel a run without ever leaving the one
 * real conversation. This own composer/input below is ONLY for the
 * standalone /agents route (no project context, no chat panel alongside
 * it) — with a second live chat input on screen at the same time as the
 * real one, which the embedded case must not do. `readOnly` hides that
 * composer and swaps the empty-state copy to point at the real chat's /run
 * command instead of a "type below" instruction that no longer applies.
 */
function Agents({ readOnly = false }) {
  const [runs, setRuns] = useState([]);
  const [pendingRuns, setPendingRuns] = useState([]); // optimistic, not yet confirmed by the backend
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [cancellingId, setCancellingId] = useState(null);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const timelineEndRef = useRef(null);
  const inputRef = useRef(null);

  const loadRuns = useCallback(async () => {
    try {
      setError(null);
      const items = await listRuns();
      setRuns(items);
      // Drop any optimistic entry once the real run shows up in the list.
      setPendingRuns((prev) => prev.filter((p) => !items.some((r) => r.workflow === p.workflow && r.runId === p.runId)));
    } catch (err) {
      setError(err.message || 'Could not load runs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRuns();
    // Poll while any run is still in flight, so status updates without a manual refresh.
    const interval = setInterval(loadRuns, 5000);
    return () => clearInterval(interval);
  }, [loadRuns]);

  // Conversation timeline: oldest first, each run rendered as a user turn
  // ("run <workflow>") followed by its assistant status-card turn.
  const timeline = useMemo(() => {
    const all = [...runs, ...pendingRuns].sort((a, b) => {
      const ta = a.startedAt ? new Date(a.startedAt).getTime() : Infinity;
      const tb = b.startedAt ? new Date(b.startedAt).getTime() : Infinity;
      return ta - tb;
    });
    return all;
  }, [runs, pendingRuns]);

  useEffect(() => {
    timelineEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [timeline.length]);

  const handleCancel = async (runId) => {
    setCancellingId(runId);
    try {
      await cancelRun(runId);
      await loadRuns();
    } catch (err) {
      setError(err.message || 'Could not cancel run');
    } finally {
      setCancellingId(null);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    const workflow = input.trim();
    if (!workflow || sending) return;

    setSending(true);
    setError(null);
    setInput('');

    const tempId = `pending-${Date.now()}`;
    setPendingRuns((prev) => [...prev, { workflowId: tempId, workflow, status: 'starting', startedAt: new Date().toISOString() }]);

    try {
      const result = await startRun(workflow);
      // Reconcile the optimistic card with the real id/status the backend returned.
      setPendingRuns((prev) => prev.map((p) => (p.workflowId === tempId
        ? { ...p, ...result, workflowId: result?.workflowId || p.workflowId, status: result?.status || 'running' }
        : p)));
      await loadRuns();
    } catch (err) {
      setPendingRuns((prev) => prev.map((p) => (p.workflowId === tempId ? { ...p, status: 'failed', error: err.message } : p)));
      setError(err.message || 'Could not start run');
    } finally {
      setSending(false);
      inputRef.current?.focus();
    }
  };

  return (
    <div className="agents-page">
      <div className="agents-header">
        <div className="agents-title-row">
          <Icon name="agents" size={22} />
          <h1 className="agents-title">Agents</h1>
        </div>
        <p className="agents-subtitle">
          {readOnly
            ? 'Trigger and watch background workflows configured in AI Foundry, right from this conversation — type '
            : 'Trigger and watch background workflows configured in AI Foundry, right from a conversation.'}
          {readOnly && <code>/run &lt;workflow&gt;</code>}
          {readOnly && ' in the chat on the left.'}
          {config.aiFoundryBaseUrl && (
            <>
              {' '}
              <a href={config.aiFoundryBaseUrl} rel="noopener noreferrer" target="_blank">
                {'Configure a new one in AI Foundry ↗'}
              </a>
            </>
          )}
        </p>
      </div>

      <div className="agents-timeline">
        {loading && timeline.length === 0 ? (
          <div className="agents-loading">
            <div className="agents-spinner" />
            <span>Loading conversation…</span>
          </div>
        ) : timeline.length === 0 ? (
          <div className="agent-message agent-message--assistant">
            <div className="agent-avatar"><Icon name="agents" size={16} /></div>
            <div className="agent-bubble agent-bubble--assistant">
              {readOnly ? (
                <>
                  {'No agent runs yet. Type '}
                  <code>{'/run deploy-staging'}</code>
                  {' (or any workflow name) in the chat on the left to kick one off.'}
                </>
              ) : (
                <>
                  {'No agent runs yet. Type a workflow name below to kick one off — e.g. '}
                  <code>deploy-staging</code>
                  {'.'}
                </>
              )}
            </div>
          </div>
        ) : (
          timeline.map((run) => (
            <React.Fragment key={run.workflowId || run.runId}>
              <div className="agent-message agent-message--user">
                <div className="agent-bubble agent-bubble--user">{`Run ${run.workflow || run.workflowId}`}</div>
              </div>
              <RunCard
                cancelling={cancellingId === run.workflowId}
                onCancel={handleCancel}
                run={run}
              />
            </React.Fragment>
          ))
        )}
        {error && (
          <div className="agent-message agent-message--assistant">
            <div className="agent-avatar"><Icon name="agents" size={16} /></div>
            <div className="agent-bubble agent-bubble--error">{error}</div>
          </div>
        )}
        <div ref={timelineEndRef} />
      </div>

      {!readOnly && (
        <form className="agents-composer" onSubmit={handleSend}>
          <input
            className="agents-composer-input"
            disabled={sending}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Type a workflow name to run…"
            ref={inputRef}
            type="text"
            value={input}
          />
          <button className="agents-composer-send" disabled={sending || !input.trim()} type="submit">
            <svg fill="none" height="16" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" viewBox="0 0 24 24" width="16">
              <line x1="22" x2="11" y1="2" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </form>
      )}
    </div>
  );
}

export default Agents;
